import { useState } from "react";
import "./chartEssay.css"
import { IpaNAllophoneChart } from "../chart/IpaNAllophoneChart";
import { TrackLanguageFeatures } from "../../world/trackLanguageFeatures";
import { loadWorldData } from "../../loader/loadWorldData";
import { segmentsByLanguage } from "../../world/segmentsByLanguage";
import { getLanguageTrackers } from "../LanguageTrackers";
import { IpaFrequencyChart } from "../chart/IpaFrequencyChart";
import { LanguageCharts } from "../LanguageCharts";
import { LanguageToLanguageCharts } from "../LanguageToLanguageCharts";
import { MultiExpando } from "../widgets/MultiExpando";
import { Say } from "../widgets/Say";
import { speakSample, hasSample } from "../chart/speakSample";
import { PSlash, PSquare } from "../widgets/Delimit";

/** essay that explains various interactive IPA charts */
export function ChartEssay() {
  const [init, setInit] = useState(false);
  const [segments, setSegments] = useState<Record<string, string>>({});
  const [trackers, setTrackers] = useState<TrackLanguageFeatures | null>(null);

  if (!init) {
    setInit(true);
    loadWorldData().then(() => {
      setSegments(segmentsByLanguage);
      setTrackers(getLanguageTrackers());
    });
  }

  return (<div className='page'>
    <h1>Phoneme Charts</h1>

    <p>
      This page contains charts for IPA
      (the <a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet">International Phonetic Alphabet</a>),
      data about the how often different phonemes occur across languages,
      the ability to look up phoneme segments by language, and tables for comparing segments between languages.
    </p>
    <p>
      Quick links to the charts on this page:
    </p>

    <ul>
      <li><a href="#ipa">IPA Charts</a></li>
      <li><a href="#occurence">Occurence of phonemes across languages</a></li>
      <li><a href="#language">Segments by language</a></li>
      <li><a href="#compare">Compare segments between languages</a></li>
    </ul>

    <p>
      Click on any of the topics below to learn more:
    </p>

    <MultiExpando title="Credits:" labels={['PHOIBLE', 'Audio']}>{[
      <Phoible key="phoible" />,
      <Audio key="audio" />,
    ]}</MultiExpando>
    <br /><br />

    <MultiExpando title="Definitions:" labels={['phoneme', 'allophone', 'segment', 'feature']}>{[
      <p className="define" key="phoneme"><b>Phoneme:</b> a set of sounds that can distinguish one word from another in a particular language.<br />
        Phonemes are often enclosed in a pair of slashes or square brackets, e.g. <PSlash>t</PSlash> or <PSquare>t</PSquare>.<br />
        For example, Hindi has the phonemes <PSlash>ʈ</PSlash>, <PSlash>ʈʰ</PSlash>, <PSlash>ɖ</PSlash>, and <PSlash>ɖʰ</PSlash>,
        so <Say>/ʈal/</Say>, <Say>/ʈʰal/</Say>, <Say>/ɖal/</Say>, and <Say>/ɖʱal/</Say> are all different words.
        English, in contrast, only has the phonemes <PSlash>t</PSlash> and <PSlash>d</PSlash>,
        so <Say say="/ˈtʰɑɫ/">tall /ˈtɑɫ/ [ˈtʰɑɫ]</Say> and <Say say="/ˈdɑɫ/">doll /ˈdɑɫ/ [ˈdɑɫ]</Say> are different words.</p>,
      <p className="define" key="allophone"><b>Allophone:</b> one of the possible sounds a speaker may use for a phoneme.<br />
        More precise pronunciations are often enclosed in square brackets, e.g. <PSquare>tʰ</PSquare>.<br />
        For example, an English speaker using a General American accent considers <PSlash>t</PSlash> a single phoneme,
        but in addition to pronouncing it <PSquare>t</PSquare> in <Say say="/ˈstɑp/">stop [ˈstɑp]</Say>,
        they pronounce it <PSquare>tʰ</PSquare> in <Say say="/ˈtʰɑp/">top [ˈtʰɑp]</Say>
        , <PSquare>ɾ</PSquare> in <Say say="/ˈbɐɾɝ/">butter [ˈbəɾɝ]</Say>, or <PSquare>ʔ</PSquare> in <Say say="/ˈbɐʔn̩/">button [ˈbəʔn̩]</Say>.
        Thus, <PSquare>tʰ</PSquare>, <PSquare>ɾ</PSquare>, and <PSquare>ʔ</PSquare> are allophones of <PSlash>t</PSlash> in a General American accent of English.</p>,
      <p className="define" key="segment"><b>Segment:</b> any small, discretely identifiable piece of speech.<br />
        This is more general than a <i>phoneme</i>, since it can include different size chunks such as a syllable.</p>,
      <p className="define" key="feature"><b>Feature:</b> a characteristic of a phoneme or word used for classification.<br />
        For example, some features of <PSlash>s</PSlash> are that it's a <i>fricative</i> (air forced between a narrow channel),&nbsp;
        <i>alveolar</i> (tongue against the alveolar ridge), and <i>voiceless</i> (the vocal chords don't vibrate),
        while some features of /z/ are <i>fricative</i>, <i>alveolar</i>, and <i>voiced</i>.</p>,
    ]}</MultiExpando>

    <h2 id="ipa">IPA Charts</h2>

    <p>
      The following chart shows a variety of phonemes using IPA,
      organized using some of their primary features.
      Clicking on supported symbols will play a recording of the corresponding sound
      (see Credits: Audio above for credits on the recordings).
      When you hover over a symbol, it will show phoneme features and (for any phoneme that appears in PHOIBLE) its allophones right below the chart
      (see Credits: PHOIBLE above for credits on the source data).
    </p>

    <IpaNAllophoneChart />
    <br />

    <p>
      Note that the consonants table includes more rows, columns, and symbols than are typically shown
      because it's intended to show a lot of the variety of common phonemes that appear in the data referenced by PHOIBLE.
      In contrast, IPA tables are often intended simply to show you the IPA symbols themselves.
      Most of the symbols in the above charts appear in at least one language in PHOIBLE.
      That said, PHOIBLE data contains over 3000 segments, so the tables clearly don't list all segments.
    </p>

    <p>
      In the second vowel chart above, vowels are arranged based on the two primary <a href="https://en.wikipedia.org/wiki/Formant">formants</a> of
      the sound chosen for use by the speech synthesizer.
      Formant F1 increases as you go down (ranging from roughly 250 to 900) while formant F2 increases going to the left (ranging from roughly 740 to 2300).
      The placement of phoneme symbols is intended to be a useful approximation, not a precise location,
      and doesn't include the higher formants, which can add additional flavor to the sound.
      See <a href="http://localhost:3000/?page=pronounce-essay">Picking Speech Phonemes</a> for more
      information on how the formants for the vowels were chosen.
    </p>

    <p>
      The final three rows list a selection of other interesting segments that otherwise don't appear in the charts.
    </p>

    <h2 id="occurence">Occurence of phonemes across languages</h2>

    <p>
      The next chart shows how often each phoneme appears in the 3000+ PHOIBLE references.
      Each phoneme either lists the percentage of references that contain the phoneme (for example, 89% contain <PSlash>p</PSlash>),
      or, if less than 0.5%, it instead lists the total number of references in parentheses (for example, <PSlash>p̪</PSlash> only has 1 reference).
    </p>

    {trackers === null ? 'loading language data...'
      : <IpaFrequencyChart segmentsByLanguage={segments} clicked={(ipa) => speakSample(ipa)} canClick={(ipa) => hasSample(ipa)} />
    }
    <br />

    <p>
      Notes on counting:
    </p>

    <p>
      The PHOIBLE sources sometimes include multiple references for the same language.
      Sometimes this is because they're for different dialects, such as <i>Spanish (Castilian)</i> and <i>Spanish (Mercian)</i>.
      Other times, however, it's simply that there are multiple sources describing the same language,
      which impacts the segment counts.
      For example, even though Swedish is the only language that uses <PSlash>ɧ</PSlash>,
      the count for that segment is 2 because there are two references for Swedish.
    </p>

    <p>
      Symbols may represent a segment, such as <PSlash>k</PSlash>, or may be combined with other symbols to represent a segment,
      such as <PSlash>kp</PSlash>, <PSlash>kʰ</PSlash>, <PSlash>kǃ</PSlash>, or <PSlash>ŋk</PSlash>.
      Since <PSlash>kp</PSlash>, <PSlash>kʰ</PSlash>, and <PSlash>kǃ</PSlash> are all listed in the charts, occurences of those segments are counted as-is.
      But, since <PSlash>ŋk</PSlash> is <i>not</i> one of the listed segments, it's instead counted toward <PSlash>k</PSlash> and <PSlash>ŋ</PSlash>.
      An example of how this can be useful is that you can see that 7% of the references include
      an ejective (ʼ) other than <PSlash>pʼ</PSlash>, <PSlash>tʼ</PSlash>, <PSlash>kʼ</PSlash>, or <PSlash>sʼ</PSlash>.
    </p>

    <h2 id="language">Segments by language</h2>

    <p>
      The next set of tables allows you to see the segments for any of the languages in PHOIBLE.
      The vowel formant chart shows arrows for diphthongs,
      drawing an arrow from <PSlash>a</PSlash> to <PSlash>ɪ</PSlash> for <PSlash>aɪ</PSlash>, for example.
      When PHOIBLE contains multiple references to the same language, they will be numbered
      (such as Amharic, Amharic 2, and Amharic 3).
      The <i>reference link</i> will take you to the PHOIBLE page for more information.
    </p>

    <p>
      You can also filter down to languages that contain certain types of segments,
      like vowel clusters (diphthongs), implosives, or tones.
      Or you can type in a custom set of features or phonemes.
      For example, to see all languages that contain both tones and clicks, type in <i>tone click</i> and pick <i>apply</i>.
      Or, to see all languages that contain both <PSlash>kp</PSlash> and <PSlash>ŋɡ</PSlash>, type in <i>kp ŋɡ</i> and pick <i>apply</i>.
    </p>

    {trackers === null ? 'loading language data...'
      : <LanguageCharts trackers={trackers} clicked={(ipa) => speakSample(ipa)} canClick={(ipa) => hasSample(ipa)} />
    }

    <h2 id="compare">Compare segments between languages</h2>

    <p>
      The next set of tables allows you to compare the segments between two language sources
      such as <i>American English</i> versus <i>French</i>.
      Or you can compare two references to the same language,
      such as <i>American English</i> versus <i>English (American)</i>.
      This can show you how various sources differ;
      for example one source uses <PSlash>i</PSlash> and <PSlash>ei</PSlash> while
      the other source uses <PSlash>iɪ</PSlash> and <PSlash>eɪ̯</PSlash> for the same phonemes.
    </p>

    <p>
      Segments that are only in the first language have a red background,
      segments that are only in the second language have a green background,
      and segments that are in both languages use the default background.
      Both languages link to the PHOIBLE source.
    </p>

    {trackers === null ? 'loading language data...'
      : <LanguageToLanguageCharts trackers={trackers} clicked={(ipa) => speakSample(ipa)} canClick={(ipa) => hasSample(ipa)} />
    }
  </div>);
}

/** describe PHOIBLE */
function Phoible() {
  return (<div className="reference">
    <span className="reftitle">PHOIBLE</span>

    <p>
      <a href="https://phoible.org">PHOIBLE</a> is a great resource for phonological data for a wide variety of languages.
      It has phoneme inventories for over 2000 languages, which includes over 3000 segment types.
      This page uses that data to show the prevalence of phoneme segments across languages and to list allophones.
      It also provides links to the source for individual languages.
    </p>

    <p className="cite">
      Moran, Steven & McCloy, Daniel (eds.) 2019.<br />
      PHOIBLE 2.0.<br />
      Jena: Max Planck Institute for the Science of Human History.<br />
      (Available online at <a href="http://phoible.org">http://phoible.org</a>. Accessed on 2021-11-17.)
    </p>

    <p>
      <b>Caveats:</b> However, as with any data set, don't assume that it gives you a 100% accurate understanding of the real world.
      It's useful to understand the biases and errors that can affect the data before drawing broad conclusions from it.
      Here are some issues to consider:
    </p>

    <ul>
      <li><b>Bias of selected languages:</b>&nbsp;
        Some languages and language families are over-represented (like Indo-European), while others are under-represented or entirely absent.</li>
      <li><b>Changing interpretations:</b>&nbsp;
        Some of the source material was published in the early 1900's, with the earliest sources published in the 1800's.
        The way linguists interpret and describe phonemes has changed a lot over the last century+.</li>
      <li><b>Imprecise notation & interpretation:</b>&nbsp;
        A symbol such as <PSlash>e</PSlash> doesn't have a precise meaning.
        Instead, it represents a range of sounds that can vary by language, dialect, accent, speaker, and context.
        What one linguist represents as <PSlash>e</PSlash>, another may represent as <PSlash>e̞</PSlash>, or <PSlash>eː</PSlash>, or <PSlash>ë</PSlash>, or <PSlash>eɪ</PSlash>, etc.
        This can cause the representation for diphthongs to vary even more than monophthongs, since they don't start or end at a precise symbol.
        For another example, see the
        article <a href="https://www.cambridge.org/core/journals/journal-of-the-international-phonetic-association/article/whats-in-the-r-a-review-of-the-usage-of-the-r-symbol-in-the-illustrations-of-the-ipa/7E1FA64F2B742A68AAF41099BB950D43">What’s in the r?</a> for
        a discussion of how the symbol <PSlash>r</PSlash> has been used for a bunch of different sounds.</li>
      <li><b>Transcription errors:</b>&nbsp;
        There's lots of opportunity for mistakes as you go from hearing sounds spoken,
        to a linguist's notes, to writing it up in a paper, to the publisher, to the printer,
        to someone transcribing it into PHOIBLE, to having it appear on this page.
        There are lots of sets of symbols that look very similar but use different characters,
        such as <PSlash>R</PSlash> and <PSlash>ʀ</PSlash>; or <PSlash>õ</PSlash> (one symbol) and <PSlash>õ</PSlash> (two symbols, o + tilde);
        or <PSlash>t</PSlash>, <PSlash>t̠</PSlash>, <PSlash>t̪</PSlash>, <PSlash>ʈ</PSlash>, <PSlash>ȶ</PSlash>, <PSlash>t̚</PSlash>, <PSlash>t͉</PSlash>, and <PSlash>t͈</PSlash>.</li>
    </ul>
  </div>);
}

/** describe where the audio comes from */
function Audio() {
  return (<div className="reference">
    <span className="reftitle">Audio</span>

    <p>
      Clicking on many of the symbols in the IPA Chart below will play recordings of the phoneme from one of the following sources:
    </p>
    <ul>
      <li>
        <a href="https://www.internationalphoneticassociation.org/IPAcharts/inter_chart_2018/IPA_2018.html">IPA Charts</a> from
        the <a href="https://www.internationalphoneticassociation.org/">International Phonetic Association</a>.
        See their <a href="https://www.internationalphoneticassociation.org/IPAcharts/inter_chart_2018/IPA_2018_about.html">About</a> page for more information.
      </li>
      <li>
        Wikipedia's <a href="https://en.wikipedia.org/wiki/IPA_consonant_chart_with_audio">IPA consonant chart with audio</a> and <a href="https://en.wikipedia.org/wiki/IPA_vowel_chart_with_audio">IPA vowel chart with audio</a>.
      </li>
    </ul>

    <p>
      Whenever you see <Say say="/ɐ ˈtɹaɪ̯ˌɛə̯ŋɡɫ̩ ˈfɑɫɤ̞o̯d baɪ̯ ˈɐndɝˌɫaɪ̯nd ˈtɛkst/">a triangle followed by underlined text</Say>, you can click the text to hear it spoken.
      This uses a speech synthesizer called <a href="https://espeak.sourceforge.net/">eSpeak</a>, tweaked with custom phonemes
      and wrapped by <a href="https://www.masswerk.at/mespeak">meSpeak.js</a>.
      Note that it runs locally, entirely in the browser, with no data sent remotely, so it can run even when you're offline.
      See <a href="?page=pronounce-essay">Picking Speech Phonemes</a> for more information about how it's used.
    </p>
  </div>);
}
